/**
 * Casting Feel Config — CASTING-FEEL lane (2026-07-09).
 *
 * WoW-style cast feel for the LOCAL player: movement slows while a real
 * (non-instant) cast is winding up. The slow is a per-tick multiplicative
 * compose in MovementSystem.move — NOT a stat mutation — so it reverts
 * automatically the tick after the cast completes/cancels and stacks
 * cleanly with haste buffs (`speedMultiplier`), race statOverrides, and
 * powerup magnitudes. Vertical physics (jump/gravity) is untouched.
 *
 * Kill switches are read BY REFERENCE each tick (the race-feel.config /
 * mobBumpConfig precedent), so a dev-override flip works live mid-session.
 *
 * Two-section file (defaults + dev override — see race-feel.config.ts).
 */

export interface CastingConfig {
  /** Master kill switch for the while-casting movement slow. */
  slowWhileCasting: boolean;
  /** Multiplicative horizontal move-speed factor while casting (0..1). */
  moveSlowMultiplier: number;
  /**
   * Minimum active-cast totalDuration (ms) for the slow to engage.
   * ProjectileBehavior pads every timeline by +100ms, so an INSTANT cast
   * (castTime 0) still registers a 100ms active cast — this gate keeps
   * instants from flicker-slowing. Real casts (200ms+ → 300ms+ padded)
   * clear it comfortably.
   */
  minSlowCastDurationMs: number;
  /**
   * Generic subtle casting glow (CASTING-FEEL #3b): abilities with a real
   * windup but NO authored `vfx.windUp` get a soft default glow on the
   * caster for the cast duration (ProjectileBehavior). The bar is the
   * star; the glow is garnish — kill it here if it ever reads busy.
   */
  windupGlow: boolean;
}

export const CASTING_DEFAULTS: CastingConfig = {
  slowWhileCasting: true,
  moveSlowMultiplier: 0.55,
  minSlowCastDurationMs: 150,
  windupGlow: true,
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to CASTING_DEFAULTS. Flip `slowWhileCasting: false` here (or mutate
 * CASTING live from the console) to restore pre-lane behavior instantly.
 */
export const CASTING_DEV_OVERRIDE: Partial<CastingConfig> = {};

export const CASTING: CastingConfig = {
  ...CASTING_DEFAULTS,
  ...CASTING_DEV_OVERRIDE,
};

/**
 * Is this active cast long enough to slow the caster? Pure gate — pass the
 * result of `CastTimelineSystem.getActiveCast(playerId)` (or undefined).
 * The kill switch is deliberately NOT folded in here: the flag setter
 * (PredictionLoop) uses this gate, while MovementSystem checks
 * `CASTING.slowWhileCasting` live so a mid-cast switch flip takes effect
 * on the very next tick.
 */
export function isSlowableCast(
  cast: { totalDuration: number } | null | undefined,
): boolean {
  return !!cast && cast.totalDuration >= CASTING.minSlowCastDurationMs;
}
